import { type ReactNode, useEffect, useRef, useState } from 'react'

interface TrackingSourceRowProps {
  actions?: ReactNode
  children: ReactNode
  className?: string
  onStackChange?: (isStacked: boolean) => void
  stackBreakpoint?: number
}

const DEFAULT_STACK_BREAKPOINT = 760

const mergeClassNames = (...classes: Array<string | undefined>) => classes.filter(Boolean).join(' ')

/**
 * Shared layout shell for all tracking source rows.
 * Keeps content/action alignment and responsive stacking behavior consistent.
 */
export const TrackingSourceRow = ({
  actions,
  children,
  className,
  onStackChange,
  stackBreakpoint = DEFAULT_STACK_BREAKPOINT
}: TrackingSourceRowProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isStacked, setIsStacked] = useState(false)

  useEffect(() => {
    const container = containerRef.current
    if (!container || typeof ResizeObserver === 'undefined') {
      return
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width || 0
      const shouldStack = width < stackBreakpoint
      setIsStacked((previous) => (previous === shouldStack ? previous : shouldStack))
    })

    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [stackBreakpoint])

  useEffect(() => {
    onStackChange?.(isStacked)
  }, [isStacked, onStackChange])

  return (
    <div
      ref={containerRef}
      className={mergeClassNames(
        'w-full',
        isStacked ? 'flex flex-col gap-2' : 'flex items-center justify-between gap-2',
        className
      )}
    >
      <div className="min-w-0 flex-1">{children}</div>
      {!!actions && (
        <div
          className={
            isStacked ? 'flex w-full items-center justify-start gap-2 flex-wrap' : 'flex items-center gap-2 self-center'
          }
        >
          {actions}
        </div>
      )}
    </div>
  )
}
